import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, X } from 'lucide-react';
import api from '../../lib/axios';
import Modal from '../../components/ui/Modal';

const schema = z.object({
  name:                  z.string().min(1, 'Required'),
  description:           z.string().optional(),
  imageUrl:              z.string().url().optional().or(z.literal('')),
  category:              z.string().optional(),
  duration_minutes:      z.coerce.number().int().min(5, 'Min 5 min').max(480),
  price_cents:           z.coerce.number().int().min(0),
  buffer_before_minutes: z.coerce.number().int().min(0).max(120),
  buffer_after_minutes:  z.coerce.number().int().min(0).max(120),
  color:                 z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#2E5BBA'),
  active:                z.boolean().default(true),
});

const COLORS = ['#2E5BBA','#7C3AED','#059669','#DC2626','#D97706','#0891B2','#DB2777','#65A30D'];

export default function ServiceEditModal({ open, service, onClose }) {
  const qc      = useQueryClient();
  const isNew   = !service?.id;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');

  const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { duration_minutes: 30, price_cents: 0, buffer_before_minutes: 0, buffer_after_minutes: 0, color: '#2E5BBA', active: true, imageUrl: '' },
  });

  const color    = watch('color');
  const imageUrl = watch('imageUrl');

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadErr('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('image', file);
      const res = await api.post('/upload/image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setValue('imageUrl', res.data.data.url, { shouldValidate: true });
    } catch (err) {
      setUploadErr(err.response?.data?.error?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (open) {
      reset(service?.id ? {
        name:                  service.name,
        description:           service.description || '',
        imageUrl:              service.image_url || '',
        category:              service.category || '',
        duration_minutes:      service.duration_minutes,
        price_cents:           service.price_cents,
        buffer_before_minutes: service.buffer_before_minutes,
        buffer_after_minutes:  service.buffer_after_minutes,
        color:                 service.color,
        active:                service.active,
      } : { duration_minutes: 30, price_cents: 0, buffer_before_minutes: 0, buffer_after_minutes: 0, color: '#2E5BBA', active: true, imageUrl: '' });
    }
  }, [open, service, reset]);

  const mut = useMutation({
    mutationFn: (data) => {
      const payload = { ...data, image_url: data.imageUrl || null };
      delete payload.imageUrl;
      return isNew
        ? api.post('/services', payload)
        : api.put(`/services/${service.id}`, payload);
    },
    onSuccess: () => { qc.invalidateQueries(['services']); onClose(); },
  });

  async function onSubmit(data) {
    await mut.mutateAsync(data);
  }

  return (
    <Modal open={open} onClose={onClose} title={isNew ? 'Add service' : 'Edit service'} size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="label">Service name</label>
          <input {...register('name')} className={`input ${errors.name ? 'border-red-400' : ''}`} placeholder="e.g. Eyebrow Threading" />
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Category</label>
            <input {...register('category')} className="input" placeholder="e.g. Threading" />
          </div>
          <div>
            <label className="label">Duration (minutes)</label>
            <input {...register('duration_minutes')} type="number" min="5" className={`input ${errors.duration_minutes ? 'border-red-400' : ''}`} />
            {errors.duration_minutes && <p className="mt-1 text-xs text-red-500">{errors.duration_minutes.message}</p>}
          </div>
        </div>

        <div>
          <label className="label">Price (in cents — e.g. 1200 = $12.00)</label>
          <input {...register('price_cents')} type="number" min="0" className="input" />
          {watch('price_cents') > 0 && (
            <p className="mt-1 text-xs text-ink-muted">
              = {(watch('price_cents') / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Buffer before (min)</label>
            <input {...register('buffer_before_minutes')} type="number" min="0" className="input" />
          </div>
          <div>
            <label className="label">Buffer after (min)</label>
            <input {...register('buffer_after_minutes')} type="number" min="0" className="input" />
          </div>
        </div>

        <div>
          <label className="label">Description</label>
          <textarea {...register('description')} rows={2} className="input resize-none" placeholder="Optional service description…" />
        </div>

        <div>
          <label className="label">Service image <span className="text-ink-muted font-normal">(optional)</span></label>
          {/* Preview */}
          {imageUrl && (
            <div className="relative mb-2 w-full h-32 rounded-lg overflow-hidden border border-surface-border">
              <img src={imageUrl} alt="preview" className="w-full h-full object-cover" onError={e => e.target.style.display='none'} />
              <button type="button" onClick={() => setValue('imageUrl', '')}
                className="absolute top-1.5 right-1.5 bg-white/80 rounded-full p-0.5 hover:bg-white text-ink-muted">
                <X size={14} />
              </button>
            </div>
          )}
          {/* Upload button + URL input row */}
          <div className="flex gap-2">
            <input
              type="url"
              placeholder="https://example.com/image.jpg"
              {...register('imageUrl')}
              className="input flex-1 text-sm"
            />
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleFileUpload} />
            <button type="button" disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="btn-secondary flex items-center gap-1.5 shrink-0 text-xs">
              <Upload size={13} />
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
          {uploadErr && <p className="mt-1 text-xs text-red-500">{uploadErr}</p>}
          <p className="text-xs text-ink-muted mt-1">Upload an image or paste a URL — displayed in the services gallery</p>
        </div>

        <div>
          <label className="label">Color</label>
          <div className="flex items-center gap-2 flex-wrap">
            {COLORS.map(c => (
              <button key={c} type="button" onClick={() => setValue('color', c)}
                className={`w-7 h-7 rounded-full transition-all ${color === c ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''}`}
                style={{ background: c }} />
            ))}
            <input type="color" value={color} onChange={e => setValue('color', e.target.value)}
              className="w-7 h-7 rounded-full border-0 p-0 cursor-pointer" title="Custom color" />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <input {...register('active')} type="checkbox" id="svc-active" className="w-4 h-4 accent-primary" />
          <label htmlFor="svc-active" className="text-sm text-ink">Active (visible to clients)</label>
        </div>

        {mut.isError && (
          <p className="text-xs text-red-500">{mut.error?.response?.data?.error?.message || 'Save failed'}</p>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-surface-border">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? 'Saving…' : isNew ? 'Create service' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
